import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ComponentProps, FC } from "react";

import { m } from "@recobra/i18n/messages";
import type { SystemTaskKey } from "@recobra/schemas/task-catalog";
import { canAssignTask, type CaseScope, isInCaseScope } from "@recobra/shared";

import { caseSummaryQueryOptions } from "../../cases";
import { UserSelect } from "../../lib";
import type { tasksApi } from "../../lib/api";
import { taskAssigneesQueryOptions } from "../task-data";
import { useTaskTypes } from "../task-data";

export const TaskAssigneePicker: FC<{
  taskType: string;
  contextId: string;
  contextType: "case" | "client";
  value: string;
  enabled: boolean;
  isSaving?: boolean;
  automaticWhenEmpty?: boolean;
  onChange: (value: string) => void;
}> = ({
  taskType,
  contextId,
  contextType,
  value,
  enabled,
  isSaving,
  automaticWhenEmpty,
  onChange,
}) => {
  const { users, isReady } = useTaskAssigneeOptions({
    taskType,
    contextId,
    contextType,
    enabled,
  });

  return (
    <UserSelect
      value={value}
      onValueChange={onChange}
      users={users.map(({ username, name }) => ({ id: username, name }))}
      placeholder={
        automaticWhenEmpty ? m.task_field_assignee_automatic() : m.task_field_assignee_unassigned()
      }
      allowEmpty
      groupLabel={m.task_field_assignee()}
      emptyMessage={m.task_field_assignee_none_eligible()}
      variant="pill"
      disabled={
        !enabled || !!isSaving || !taskType || (!isReady && (!value || !!automaticWhenEmpty))
      }
      isBusy={isSaving}
      ariaLabel={m.task_field_assignee()}
    />
  );
};

const useTaskAssigneeOptions = ({
  taskType,
  contextId,
  contextType,
  enabled,
}: Pick<
  ComponentProps<typeof TaskAssigneePicker>,
  "taskType" | "contextId" | "contextType" | "enabled"
>) => {
  const configured = useTaskTypes().data?.get(taskType);
  const system = configured?.system ?? null;
  const queryClient = useQueryClient();
  const { data: usersData } = useQuery({
    ...taskAssigneesQueryOptions,
    enabled,
  });
  const { data: caseSummary } = useQuery({
    ...caseSummaryQueryOptions(queryClient, contextId),
    enabled: enabled && contextType === "case" && system === "provide_case_documents",
  });
  const caseScope: CaseScope | null = caseSummary
    ? { clientId: caseSummary.clientId, branchId: caseSummary.branchId }
    : null;
  const allowedGroups = configured?.allowedGroups;
  const users = eligibleTaskAssignees(system, usersData?.users ?? [], caseScope, allowedGroups);

  const isReady = !!usersData && allowedGroups !== undefined;
  return { users: isReady ? users : [], isReady };
};

type AssigneeCandidate = NonNullable<
  Awaited<ReturnType<typeof tasksApi.listAssignees>>
>["users"][number];

const eligibleTaskAssignees = (
  system: SystemTaskKey | null,
  allUsers: AssigneeCandidate[],
  caseScope: CaseScope | null,
  allowedGroups: readonly string[] | undefined,
): AssigneeCandidate[] => {
  if (!allowedGroups) return [];
  return allUsers.filter((user) =>
    canAssignTask(
      system,
      user.groups,
      allowedGroups,
      caseScope !== null && isInCaseScope(user, caseScope),
    ),
  );
};
